import { Injectable } from "@angular/core";
import { InvoiceUpdateData, MicroBilling } from "../../../pojo/Pricing";
import { DataAccess } from "../../../dataaccess/data.access";
import { S25Util } from "../../../util/s25-util";

@Injectable({ providedIn: "root" })
export class EventPricingService {
    async getInvoices(eventIds: number[]): Promise<MicroBilling> {
        const params = DataAccess.params({
            eventId: eventIds.join(" "),
            include: INVOICE_INCLUDES.join(" "),
            // Only a read asks for expandedInfo: the lookups it feeds describe the event, not one invoice.
            expand: "T",
            include_reference_set: "T",
            caller: "PricingService.getInvoices",
        });
        const url = `/micro/event/billingCustom.json?${params}`;
        const data = await DataAccess.get<{ content: MicroBilling }>(url);
        return data.content;
    }

    async postBillingCustom(createData: InvoiceCreateData): Promise<{ content: MicroBilling }> {
        const payload = {
            content: {
                data: [
                    {
                        op: "params",
                        ...createData,
                    },
                ],
            },
        };
        return DataAccess.post(DataAccess.injectCaller(invoiceWriteUrl(), "PricingService.postBillingCustom"), payload);
    }

    async deleteInvoice(invoiceId: number): Promise<void> {
        return DataAccess.delete(
            DataAccess.injectCaller(`/micro/event/${invoiceId}/billingCustom.json`, "PricingService.deleteInvoice"),
        );
    }

    async putBillingCustom(
        updateData: Partial<InvoiceUpdateData>,
        evBillId: number,
    ): Promise<{ content: MicroBilling }> {
        const payload = {
            content: {
                data: [
                    {
                        op: "all",
                        billId: evBillId,
                        ...withReservationListFlag(updateData),
                    },
                ],
            },
        };
        return DataAccess.put(DataAccess.injectCaller(invoiceWriteUrl(), "PricingService.putBillingCustom"), payload);
    }

    // Each update carries its own billId and op, so one request can change two invoices at once.
    async putBillingCustomBatch(updates: Partial<InvoiceUpdateData>[]): Promise<{ content: MicroBilling }> {
        return DataAccess.put(DataAccess.injectCaller(invoiceWriteUrl(), "PricingService.putBillingCustomBatch"), {
            content: { data: updates },
        });
    }
}

/**
 * A write answers with the invoices it changed, and the client merges them into the ones a read
 * returned, so both have to describe an invoice the same way.
 */
function invoiceWriteUrl() {
    return `/micro/event/billingCustom.json?${DataAccess.params({ include: INVOICE_INCLUDES.join(" ") })}`;
}

/**
 * emptyRsrvList is the name this app has always sent; the proc reads emptyReservationList today.
 * Both go out with the same value until it is settled which one the API means to keep, and the
 * unused one is ignored. Drop the name that loses.
 */
export function emptyReservationListFields(empty: boolean) {
    return { emptyRsrvList: empty, emptyReservationList: empty };
}

/**
 * A stored empty reservation list is sticky: a later request that leaves the flag out has it filled
 * back in from the invoice, which discards the list the request was sent with.
 */
function withReservationListFlag(updateData: Partial<InvoiceUpdateData>): Partial<InvoiceUpdateData> {
    if (!updateData.rsrvIdList?.length) return updateData;
    if (S25Util.isDefined(updateData.emptyReservationList ?? updateData.emptyRsrvList)) return updateData;
    return { ...updateData, ...emptyReservationListFields(false) };
}

/** The fields the API accepts when creating an invoice, which are not the ones it accepts on update. */
export type InvoiceCreateData = {
    billName: string;
    eventIdList: number[];
    includeEventType: boolean;
    includeRequirements: boolean;
    includeResources: boolean;
    includeSpaces: boolean;
    requirementIdList?: number[];
    rsrvIdList?: number[];
    startDt?: Date;
    endDt?: Date;
};

type BillingCustomInclude =
    | "rate_info"
    | "occurrences"
    | "requirements"
    | "event_type"
    | "organizations"
    | "profiles"
    | "all";

const INVOICE_INCLUDES: BillingCustomInclude[] = [
    "rate_info",
    "occurrences",
    "requirements",
    "event_type",
    "organizations",
];
